import { message, Modal } from 'antd';
import $ from '../../../common/AjaxRequest';
import API_URL from '../../../common/url';
import React from 'react';

/**
 * 文件夹下载工具模块（OSS 异步打包 + Java 中转代理 + 断点续传）
 *
 * 流程：
 * 1. queryFolderSize → 大小校验
 * 2. 确认弹窗 → addFileLog（操作日志）
 * 3. createFolderZipTask → 发起 OSS 异步打包，返回 taskId
 * 4. 轮询 getZipKey → 打包完成返回 zipKey
 * 5. 查询 getLastOffset → 获取上次下载偏移（续传）
 * 6. showSaveFilePicker → 用户选择本地保存路径
 * 7. downloadZipProxy → fetch 流式下载 + 直接写入磁盘（不占内存）
 * 8. 进度弹窗显示 + 暂停按钮 + 定时上报偏移量
 */

const PROGRESS_REPORT_INTERVAL = 800; // 进度上报间隔（ms）
const POLL_INTERVAL = 2000;           // 打包轮询间隔（ms）
const MAX_POLL_TIMES = 300;           // 最大轮询次数（10分钟）

/* ---------- 全局状态：当前进行中的下载，用于暂停 ---------- */
let currentReader = null;
let currentReportTimer = null;
let currentDownloadInfo = null;
let currentWritable = null;            // FileSystemWritableFileStream，暂停时不关闭，续传复用
let currentAbortController = null;     // 用于暂停时中断 fetch 流（抛出 AbortError）

/* ---------- 下载进度弹窗 ---------- */
let progressModal = null;

function showProgressModal(catalogName, startOffset) {
    // 用 React.createElement 创建组件，Modal.info 不接受原生 DOM 元素
    const content = React.createElement('div', { style: { margin: '12px 0' } },
        React.createElement('p', { style: { fontWeight: 'bold', marginBottom: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } },
            catalogName + '.zip'
        ),
        React.createElement('div', { style: { background: '#f0f0f0', borderRadius: 4, height: 18, overflow: 'hidden' } },
            React.createElement('div', { id: 'dp-fill', style: { background: '#1890ff', height: '100%', width: '0%', transition: 'width 0.3s ease' } })
        ),
        React.createElement('p', { id: 'dp-text', style: { textAlign: 'center', marginTop: 8, color: '#666' } },
            startOffset > 0 ? '续传中...' : '准备中...'
        ),
        React.createElement('p', { id: 'dp-speed', style: { textAlign: 'center', marginTop: 2, color: '#999', fontSize: 12 } })
    );

    progressModal = Modal.info({
        title: '下载文件',
        width: 480,
        okText: '暂 停',
        onOk: () => {
            pauseDownload();
            return false;
        },
        content,
    });
}

function updateProgressModal(percent, downloadedStr, totalStr, speedStr) {
    if (!progressModal) return;
    const fill = document.getElementById('dp-fill');
    const text = document.getElementById('dp-text');
    const speed = document.getElementById('dp-speed');
    if (fill) fill.style.width = Math.min(percent, 100) + '%';
    if (text) text.textContent = `${downloadedStr} / ${totalStr}`;
    if (speed) speed.textContent = speedStr;
}

function hideProgressModal() {
    if (progressModal) {
        progressModal.destroy();
        progressModal = null;
    }
}

// ================================================================
//  公共 API
// ================================================================

/**
 * 检查文件夹大小并确认下载
 */
export function checkAndDownloadFolder(catalogId, catalogName, onStartDownload) {
    const options = {
        method: 'POST',
        url: API_URL.deptFile.queryFolderSize,
        data: { catalogId },
        dataType: 'json',
        doneResult: (res) => {
            const { totalSize, canDownload } = res;
            if (!canDownload) {
                message.error('文件夹下载最大支持15G，无法下载');
                return;
            }
            const sizeStr = formatFileSize(totalSize);
            Modal.confirm({
                title: '确认下载',
                content: `确定要下载文件夹"${catalogName}"吗？\n文件夹大小：${sizeStr}`,
                onOk: () => {
                    if (onStartDownload) onStartDownload(catalogId, catalogName);
                    startFolderDownload(catalogId, catalogName);
                },
            });
        },
        errorResult: () => message.error('查询文件夹大小失败'),
    };
    $.sendRequest(options);
}

/**
 * 开始完整异步下载流程
 */
export async function startFolderDownload(catalogId, catalogName) {
    try {
        message.loading('正在准备打包，请稍候...', 0, 'ZIP_PACKING');

        const taskRes = await ajaxPost(API_URL.deptFile.createFolderZipTask, { catalogId });
        const taskId = taskRes.taskId;
        let zipKey = taskRes.zipKey || null;

        if (zipKey) {
            message.destroy('ZIP_PACKING');
        } else {
            let pollTimes = 0;
            while (!zipKey && pollTimes < MAX_POLL_TIMES) {
                await sleep(POLL_INTERVAL);
                const pollRes = await ajaxPost(API_URL.deptFile.getZipKey, {
                    taskId, curUserId: getUserId(),
                });
                zipKey = pollRes.zipKey;
                pollTimes++;
            }
            message.destroy('ZIP_PACKING');
            if (!zipKey) {
                message.error('文件夹打包超时，请重新尝试');
                return;
            }
        }

        await doProxyDownload(zipKey, catalogName);
    } catch (e) {
        message.destroy('ZIP_PACKING');
        message.error('下载失败：' + (e.message || '未知错误'));
    }
}

/**
 * 暂停当前下载
 */
export function pauseDownload() {
    // 使用 AbortController 中断 fetch 流 → reader.read() 抛出 AbortError
    // 由 catch 块捕获，走暂停路径（不关闭 writable、不弹完成提示）
    if (currentAbortController) {
        currentAbortController.abort();
        currentAbortController = null;
    }
    if (currentReportTimer) {
        clearInterval(currentReportTimer);
        currentReportTimer = null;
    }
    // 注意：currentWritable 不关闭，留待续传复用
    updateProgressModal(0, '已暂停', '', '点击"暂 停"按钮可续传');
}

/**
 * 获取当前下载信息
 */
export function getCurrentDownloadInfo() {
    return currentDownloadInfo;
}

// ================================================================
//  核心下载逻辑
// ================================================================

/**
 * 执行中转代理下载
 * 使用 File System Access API 直接写磁盘，不占内存，避免 render process gone
 */
async function doProxyDownload(zipKey, catalogName) {
    // 1) 查询断点偏移
    const offsetRes = await ajaxPost(API_URL.deptFile.getLastOffset, {
        curUserId: getUserId(), zipKey,
    });
    const startOffset = offsetRes.offset || 0;

    // 2) 构建请求头
    const tokenName = localStorage.tokenName || '';
    const tokenValue = localStorage.tokenValue || '';
    const loginType = localStorage.loginType || 'ADFS';
    const headers = { [tokenName]: tokenValue, loginType };
    if (startOffset > 0) headers['Range'] = `bytes=${startOffset}-`;

    // 3) 获取文件句柄（三种场景）
    //    放在 fetch 之前，避免先拉数据再让用户等对话框
    let fileHandle;
    let isResumeFile = false; // 跨会话续传：文件已存在，不能截断

    if (currentWritable) {
        // 场景 A：同会话续传（暂停后没关页面），复用已有 writable，跳过文件选择
        fileHandle = 'REUSE';
    } else if (startOffset > 0 && window.showOpenFilePicker) {
        // 场景 B：跨会话续传（关了页面重开），打开已有文件，不截断
        isResumeFile = true;
        try {
            [fileHandle] = await window.showOpenFilePicker({
                types: [{
                    description: 'ZIP 文件',
                    accept: { 'application/zip': ['.zip'] },
                }],
                mode: 'readwrite',
            });
        } catch (e) {
            if (e.name === 'AbortError') return;
            throw e;
        }
    } else if (window.showSaveFilePicker) {
        // 场景 C：全新下载
        try {
            fileHandle = await window.showSaveFilePicker({
                suggestedName: `${catalogName}.zip`,
                types: [{
                    description: 'ZIP 文件',
                    accept: { 'application/zip': ['.zip'] },
                }],
            });
        } catch (e) {
            if (e.name === 'AbortError') return;
            throw e;
        }
    }

    // 4) 发起 fetch（传入 AbortController signal，暂停时中断）
    const abortController = new AbortController();
    currentAbortController = abortController;
    const url = `${API_URL.deptFile.downloadZipProxy}?curUserId=${getUserId()}&zipKey=${encodeURIComponent(zipKey)}`;
    const resp = await fetch(url, { headers, credentials: 'include', signal: abortController.signal });
    if (!resp.ok && resp.status !== 206) {
        throw new Error('下载请求失败: ' + resp.status);
    }

    // 5) 获取总大小
    const contentRange = resp.headers.get('Content-Range');
    let totalSize = 0;
    if (contentRange) {
        totalSize = parseInt(contentRange.split('/')[1], 10);
    } else {
        totalSize = parseInt(resp.headers.get('Content-Length') || '0', 10);
    }

    const reader = resp.body.getReader();
    currentReader = reader;
    currentDownloadInfo = { zipKey, catalogName };

    // 6) 显示进度弹窗
    showProgressModal(catalogName, startOffset);

    // 7) 创建 writable（续传不截断文件）
    let downloaded = startOffset;
    let lastReported = startOffset;
    let lastReportTime = Date.now();

    if (fileHandle === 'REUSE') {
        // 同会话续传：writable 还开着，seek 到断点
        if (startOffset > 0) {
            try { await currentWritable.seek(startOffset); } catch (e) { /* 忽略 */ }
        }
    } else if (fileHandle) {
        if (isResumeFile) {
            // 跨会话续传：打开已有文件，保留已下载的数据
            currentWritable = await fileHandle.createWritable({ keepExistingData: true });
            await currentWritable.seek(startOffset);
        } else {
            // 全新文件
            currentWritable = await fileHandle.createWritable();
        }
    }

    // 定时器：更新 UI + 上报偏移
    currentReportTimer = setInterval(() => {
        const now = Date.now();
        const elapsed = (now - lastReportTime) / 1000;
        const bytesSinceReport = downloaded - lastReported;
        const speed = elapsed > 0 ? bytesSinceReport / elapsed : 0;

        updateProgressModal(
            totalSize > 0 ? Math.round((downloaded / totalSize) * 100) : 0,
            formatFileSize(downloaded), formatFileSize(totalSize), formatSpeed(speed)
        );

        ajaxPost(API_URL.deptFile.uploadDownloadOffset, {
            curUserId: getUserId(), zipKey, offset: downloaded, totalSize,
        });

        lastReported = downloaded;
        lastReportTime = now;
    }, PROGRESS_REPORT_INTERVAL);

    try {
        if (currentWritable) {
            // 方案 A：File System Access API → 直接写磁盘，不占内存
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                await currentWritable.write(value);
                downloaded += value.length;
            }
            await currentWritable.close();
            currentWritable = null;
        } else {
            // 方案 B：降级到 Blob 方式（不支持 File System Access API 的浏览器）
            const chunks = [];
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                chunks.push(value);
                downloaded += value.length;
            }
            const blob = new Blob(chunks);
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = `${catalogName}.zip`;
            a.click();
            URL.revokeObjectURL(a.href);
        }
    } catch (e) {
        if (e.name === 'AbortError' || (e.message && e.message.indexOf('abort') !== -1)) {
            return; // 暂停，writable 保持打开
        }
        // 非暂停异常：关闭 writable，避免下次复用已损坏的流
        if (currentWritable) {
            try { await currentWritable.close(); } catch (ce) { /* ignore */ }
            currentWritable = null;
        }
        throw e;
    } finally {
        clearInterval(currentReportTimer);
        currentReportTimer = null;
        currentReader = null;
        currentAbortController = null;
        hideProgressModal();
    }

    message.success(`文件夹"${catalogName}"下载完成`);
    currentDownloadInfo = null;
}

// ================================================================
//  工具函数
// ================================================================

function getUserId() {
    return sessionStorage.userId || '';
}

function ajaxPost(url, data) {
    return new Promise((resolve, reject) => {
        const options = {
            method: 'POST', url, data, dataType: 'json',
            doneResult: resolve,
            errorResult: (err) => {
                const msg = typeof err === 'string' ? err : (err && err.message ? err.message : '请求失败');
                reject(new Error(msg));
            },
        };
        $.sendRequest(options);
    });
}

function sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

function formatFileSize(bytes) {
    if (bytes === 0) return '0B';
    const k = 1024;
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return (bytes / Math.pow(k, i)).toFixed(2) + units[i];
}

function formatSpeed(bytesPerSecond) {
    if (bytesPerSecond <= 0) return '';
    const k = 1024;
    const units = ['B/s', 'KB/s', 'MB/s', 'GB/s'];
    const i = Math.floor(Math.log(bytesPerSecond) / Math.log(k));
    return (bytesPerSecond / Math.pow(k, i)).toFixed(1) + units[i];
}

export { formatFileSize };




// WEBPACK FOOTER //
// ./src/components/documentManagement/departmentalDocuments/FolderDownload.js